import { useAuth } from "@/features/auth";
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    Button,
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
    Icon,
    Separator,
    Skeleton,
} from "@/shared/components";
import { ErrorBoundary, ErrorDisplay } from "@/shared/components/error";
import { useNotificationPermissions, useSendNotifications } from "@/shared/hooks";
import { useNavigate } from "react-router";

export default function ProfilePage() {
    const { user, isLoading } = useAuth();
    const navigate = useNavigate();
    const sendNotification = useSendNotifications();
    const [hasPermissions, requestPermissions] = useNotificationPermissions();

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Skeleton className="h-12 w-12" />
            </div>
        );
    }

    if (!user) {
        return null;
    }

    const name = user.name ?? user.username ?? "User";
    const email = user.username || "";
    const initials = name
        .split(" ")
        .map((part: string) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    const profilePicture = (user.idTokenClaims?.picture as string) || "";

    const sendTestNotification = () => sendNotification("Test", "Dit is een test-notificatie", false);

    return (
        <ErrorBoundary
            fallback={(error, reset) => (
                <ErrorDisplay error={error} variant="section" title="Fout bij het laden van het profiel" onRetry={reset} />
            )}
        >
            <div className="container flex items-start p-20">
                <Card className="flex grow">
                    <CardHeader>
                        <div className="flex items-center space-x-4">
                            <Avatar className="h-20 w-20">
                                <AvatarImage src={profilePicture} alt={name} />
                                <AvatarFallback className="text-xl">{initials}</AvatarFallback>
                            </Avatar>
                            <div>
                                <CardTitle className="text-xxl">{name}</CardTitle>
                                <CardDescription className="text-s mt-1 flex items-center">
                                    <Icon name="mail" className="text-muted-foreground" />
                                    {email}
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-4">
                            <h3 className="text-l font-medium">Account Information</h3>
                            <Separator />

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div className="flex items-center space-x-2">
                                    <Icon name="user" className="text-muted-foreground" />
                                    <span className="text-s text-muted-foreground">Full Name:</span>
                                    <span className="text-s font-medium">{name}</span>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <Icon name="mail" className="text-muted-foreground" />
                                    <span className="text-s text-muted-foreground">Email:</span>
                                    <span className="text-s font-medium">{email}</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-l font-medium">Test Notifications</h3>
                            <Separator />

                            <div className="flex flex-col gap-4">
                                <div>
                                    <Button onClick={sendTestNotification}>
                                        <Icon name="send" />
                                        Verstuur test notificatie
                                    </Button>
                                    <p className="text-m text-content-emphasis font-medium">
                                        SEIN stuurt u <u>{hasPermissions ? "" : "geen"}</u> notificaties van belangrijke updates
                                    </p>
                                    {!hasPermissions && <Button onClick={requestPermissions}>Ik wil wel notificaties</Button>}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter className="flex justify-end">
                        <Button onClick={() => navigate(-1)} variant="tertiary">
                            Back
                        </Button>
                    </CardFooter>
                </Card>
            </div>
        </ErrorBoundary>
    );
}
